import React, { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { QualityScore, calculateQualityScore } from '@/components/ui/quality-score'
import { DiffText, MarkdownDiff, TagsDiff } from '@/components/ui/diff-text'
import { Check, X, Edit3, Eye, Sparkles } from 'lucide-react'

interface FieldImprovementProps {
  type: 'question' | 'answer' | 'tags' | 'variations'
  before: string | string[]
  after: string | string[]
  onToggle?: (selected: boolean, value: any) => void
  onEdit?: () => void
  selected?: boolean
  autoAccept?: boolean
  disabled?: boolean
}

export function FieldImprovement({
  type,
  before,
  after,
  onToggle,
  onEdit,
  selected = false,
  autoAccept = false,
  disabled = false
}: FieldImprovementProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  // For tags: track which individual tags are selected
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set(after as string[]))

  // Get field-specific configuration
  const getFieldConfig = () => {
    switch (type) {
      case 'question':
        return {
          icon: <span className="text-lg">🎯</span>,
          label: 'Frage',
          color: 'blue',
          description: 'Klarere, suchfreundlichere Fragestellung'
        }
      case 'answer':
        return {
          icon: <span className="text-lg">✍️</span>,
          label: 'Antwort',
          color: 'green',
          description: 'Bessere Struktur und Lesbarkeit'
        }
      case 'tags':
        return {
          icon: <span className="text-lg">🏷️</span>,
          label: 'Tags',
          color: 'purple',
          description: 'Präzisere Kategorisierung'
        }
      case 'variations':
        return {
          icon: <span className="text-lg">🔄</span>,
          label: 'Varianten',
          color: 'orange',
          description: 'Mehr Suchmöglichkeiten'
        }
      default:
        return { icon: null, label: 'Field', color: 'gray', description: '' }
    }
  }

  const config = getFieldConfig()
  const beforeStr = Array.isArray(before) ? before.join(', ') : before
  const afterStr = Array.isArray(after) ? after.join(', ') : after

  // Calculate quality scores
  const qualityScores = calculateQualityScore(beforeStr, afterStr, type)

  // Determine if this is a significant improvement
  const isSignificantImprovement = qualityScores.after - qualityScores.before >= 20

  return (
    <Card className={`border-2 transition-all duration-300 ${
      selected
        ? 'border-green-400 bg-green-50/20'
        : isSignificantImprovement
        ? 'border-green-200 bg-green-50/10'
        : 'border-gray-200 bg-gray-50/10'
    }`}>
      <CardContent className="p-4 space-y-3">
        {/* Header with Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            {config.icon}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{config.label}</span>
                {isSignificantImprovement && (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                    +{qualityScores.after - qualityScores.before}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{config.description}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-8 w-8 p-0"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Switch
              checked={selected}
              onCheckedChange={(checked) => {
                // For tags, use filtered selection; for others use full 'after' value
                const valueToSend = type === 'tags' ? Array.from(selectedTags) : after
                onToggle?.(checked, valueToSend)
              }}
              disabled={disabled}
            />
          </div>
        </div>

        {/* Content Preview - Short */}
        {!isExpanded && (
          <div className="text-sm">
            {type === 'tags' ? (
              <div className="flex flex-wrap gap-1">
                {(after as string[]).slice(0, 5).map((tag, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
                {(after as string[]).length > 5 && (
                  <Badge variant="secondary" className="text-xs">
                    +{(after as string[]).length - 5}
                  </Badge>
                )}
              </div>
            ) : type === 'variations' ? (
              <span className="text-muted-foreground">
                {(after as string[]).length} Frage-Variationen
              </span>
            ) : (
              <span className="text-muted-foreground line-clamp-2">
                {afterStr.substring(0, 200) + (afterStr.length > 200 ? '...' : '')}
              </span>
            )}
          </div>
        )}

        {/* Expanded View */}
        {isExpanded && (
          <div className="space-y-3 pt-2 border-t">
            <QualityScore
              before={qualityScores.before}
              after={qualityScores.after}
              label={config.label}
              icon={config.icon}
              showProgress={true}
            />

            {type === 'answer' ? (
              <MarkdownDiff
                before={beforeStr}
                after={afterStr}
                className="text-sm"
              />
            ) : type === 'tags' ? (
              <div className="space-y-3">
                <div>
                  <div className="text-xs font-medium text-red-600 mb-2">Vorher</div>
                  <div className="flex flex-wrap gap-1">
                    {(before as string[]).map((tag, i) => (
                      <Badge key={i} variant="outline" className="text-xs bg-red-50 border-red-200">
                        {tag}
                      </Badge>
                    ))}
                    {(before as string[]).length === 0 && (
                      <span className="text-xs italic text-muted-foreground">Keine Tags</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-green-600 mb-2 flex items-center justify-between">
                    <span>Nachher (klicken zum Entfernen)</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {selectedTags.size} / {(after as string[]).length} ausgewählt
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(after as string[]).map((tag, i) => {
                      const isSelected = selectedTags.has(tag)
                      return (
                        <Badge
                          key={i}
                          variant={isSelected ? "secondary" : "outline"}
                          className={`text-xs cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-green-100 border-green-300 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 border-gray-300 text-gray-600 opacity-50 hover:opacity-70 line-through'
                          }`}
                          onClick={() => {
                            const newSelected = new Set(selectedTags)
                            if (isSelected) {
                              newSelected.delete(tag)
                            } else {
                              newSelected.add(tag)
                            }
                            setSelectedTags(newSelected)
                            // Update parent with filtered tags
                            if (selected) {
                              onToggle?.(true, Array.from(newSelected))
                            }
                          }}
                        >
                          {tag}
                          {!isSelected && <X className="ml-1 h-3 w-3" />}
                        </Badge>
                      )
                    })}
                  </div>
                </div>
              </div>
            ) : type === 'question' || type === 'variations' ? (
              // Clear before/after layout for questions and variations
              <div className="space-y-3">
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="text-xs font-semibold text-red-700 mb-2 flex items-center gap-1">
                    <span>❌</span>
                    <span>Vorher</span>
                  </div>
                  <div className="text-sm text-red-900">
                    {type === 'variations' ? (
                      <div className="space-y-1">
                        {(before as string[]).length > 0 ? (
                          (before as string[]).map((v, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <span className="text-xs text-red-600 font-mono">{i + 1}.</span>
                              <span>{v}</span>
                            </div>
                          ))
                        ) : (
                          <span className="text-xs italic text-red-600">Keine Variationen</span>
                        )}
                      </div>
                    ) : (
                      beforeStr
                    )}
                  </div>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                    <span>✅</span>
                    <span>Nachher</span>
                  </div>
                  <div className="text-sm text-green-900">
                    {type === 'variations' ? (
                      <div className="space-y-1">
                        {(after as string[]).map((v, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <span className="text-xs text-green-600 font-mono">{i + 1}.</span>
                            <span>{v}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      afterStr
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <DiffText
                before={beforeStr}
                after={afterStr}
                className="text-sm"
                showInline={beforeStr.length < 200}
              />
            )}
          </div>
        )}

        {/* Recommendation indicator */}
        {autoAccept && isSignificantImprovement && !selected && (
          <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 p-2 rounded">
            <Sparkles className="h-3 w-3" />
            <span>Empfehlung: Hohe Verbesserungsqualität</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// Container component for multiple field improvements
export function FieldImprovementsContainer({
  improvements,
  onFieldAccept,
  onFieldReject,
  disabled = false
}: {
  improvements: Array<{
    type: 'question' | 'answer' | 'tags' | 'variations'
    before: string | string[]
    after: string | string[]
    autoAccept?: boolean
  }>
  onFieldAccept?: (type: string, value: any) => void
  onFieldReject?: (type: string) => void
  disabled?: boolean
}) {
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set())

  const handleFieldToggle = (type: string, selected: boolean, value: any) => {
    if (selected) {
      setSelectedFields(prev => new Set([...prev, type]))
      onFieldAccept?.(type, value)
    } else {
      setSelectedFields(prev => {
        const newSet = new Set(prev)
        newSet.delete(type)
        return newSet
      })
      onFieldReject?.(type)
    }
  }

  const totalImprovement = improvements.reduce((acc, imp) => {
    const beforeStr = Array.isArray(imp.before) ? imp.before.join(', ') : imp.before
    const afterStr = Array.isArray(imp.after) ? imp.after.join(', ') : imp.after
    const scores = calculateQualityScore(beforeStr, afterStr, imp.type)
    return acc + (scores.after - scores.before)
  }, 0)

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border">
        <div>
          <h3 className="font-semibold text-lg">Enhancement-Vorschläge</h3>
          <p className="text-sm text-muted-foreground">
            Toggle aktivieren um Änderungen zu übernehmen
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-green-600">+{totalImprovement}</div>
          <div className="text-xs text-muted-foreground">Qualitätspunkte gesamt</div>
        </div>
      </div>

      {/* Field Improvements */}
      <div className="space-y-3">
        {improvements.map((improvement) => (
          <FieldImprovement
            key={improvement.type}
            type={improvement.type}
            before={improvement.before}
            after={improvement.after}
            selected={selectedFields.has(improvement.type)}
            autoAccept={improvement.autoAccept}
            disabled={disabled}
            onToggle={(selected, value) => handleFieldToggle(improvement.type, selected, value)}
          />
        ))}
      </div>
    </div>
  )
}